import { FC, useMemo, useRef, useState, useEffect, useCallback } from "react";
import { styled } from "@mui/material/styles";
import { useTranslation } from "next-i18next";
import * as htmlToImage from "html-to-image";
import FileSaver from "file-saver";
import Image from "next/legacy/image";
import dayjs, { Dayjs } from "dayjs";
import { useSnackbar } from "notistack";
import { useCopyToClipboard } from "react-use";

import IconUndraw from "@/public/img/points/icon-undraw.svg";
import IconVip from "@/public/img/points/icon-vip.svg";
import IconPresent from "@/public/img/points/icon-present.svg";
import IconSave from "@/public/img/points/icon-save.svg";
import IconArrowToggle from "@/public/img/points/icon-arrow-toggle.svg";

import { WlCertificate } from "@/components/wl-certificate";

import { goodsOrderApi, getUnbrewReceipt } from "@/utils/api";

import { PointsBillInfo, PointsBrewingInfo } from "@/types/points";
import { formatThousand } from "@/utils/util";
import { SubGoodsTypeEnum } from "@/constants/mall";

interface RecordProps {
  className?: string;
  info: PointsBillInfo;
  isMobile?: boolean;
}

interface GoodsOrderInfo {
  extraWhitelistMsg: {
    discordId: string;
  };
  goodsNumber: string;
  goodsTime: string;
  goodsType: string;
  isSuccess: boolean;
  subGoodsType;
}

const _Record: FC<RecordProps> = ({ info, className, isMobile }) => {
  const [unbrewInfos, setUnbrewInfos] = useState<Array<PointsBrewingInfo>>();
  const [goodsInfo, setGoodsInfo] = useState<GoodsOrderInfo>();
  const [showDetail, setShowDetail] = useState(false);

  const isOrder = useMemo(
    () => info.reason === "REASON_CONSUME",
    [info.reason]
  );
  const recordClassify = useMemo(() => {
    switch (info.reason) {
      // 兑换
      case "REASON_CONSUME":
        return {
          type: "redeem",
          icon: IconPresent,
          bgColor: "rgba(0, 150, 255, 0.1)",
          points: `-${formatThousand(info.outcome)}`,
        };
      // 开桶
      case "REASON_TAP_BARREL":
        return {
          type: "undraw",
          icon: IconUndraw,
          bgColor: "rgba(255, 102, 153, 0.1)",
          points: `+${formatThousand(info.income)}`,
          add: true,
        };
      // 积分补偿
      case "REASON_COMPENSATION":
        return {
          type: "undraw",
          icon: IconUndraw,
          bgColor: "rgba(255, 102, 153, 0.1)",
          points: `+${formatThousand(info.income)}`,
          noDetail: true,
          add: true,
        };
      default:
        return {
          type: "undraw",
          icon: IconUndraw,
          bgColor: "rgba(255, 102, 153, 0.1)",
        };
    }
  }, [info]);

  const toggleDetailHandler = () => {
    setShowDetail(!showDetail);
  };

  const getOrderDetail = useCallback(async () => {
    const res = await goodsOrderApi(info.id);
    if (res.err) return;
    setGoodsInfo(res.data);
  }, [info.id]);

  const getUnbrewDetail = useCallback(async () => {
    const res = await getUnbrewReceipt(info.id);
    if (res.err) return;
    // 酿造得分 res.data.tokenIncome
    // 溢出奖励+任务奖励 res.data.income

    setUnbrewInfos([...res.data.tokenIncome, ...res.data.income]);
  }, [info.id]);

  useEffect(() => {
    if (!showDetail) return;
    if (isOrder && !goodsInfo) {
      getOrderDetail();
      return;
    }
    if (!isOrder && !unbrewInfos) {
      getUnbrewDetail();
    }
  }, [
    showDetail,
    goodsInfo,
    unbrewInfos,
    getOrderDetail,
    getUnbrewDetail,
    isOrder,
  ]);

  return (
    <figure className={`m-0 ${className}`}>
      <div
        className={`py-[20px] h-[80px] flex items-center ${
          recordClassify.noDetail ? "cursor-default" : "cursor-pointer"
        }`}
        onClick={recordClassify.noDetail ? null : toggleDetailHandler}
      >
        <div
          className="flex items-center justify-center w-[40px] h-[40px] rounded-full"
          style={{ backgroundColor: recordClassify.bgColor }}
        >
          <recordClassify.icon className="w-[28px] h-[28px]" />
        </div>
        <div className="flex-1 ml-[12px]">
          <div className="flex items-center">
            <h3 className="flex-1 m-0 text-[14px] leading-[16px] text-black font-medium">
              {info.desc}
            </h3>
            <div className="flex items-center">
              <div
                className={`${
                  recordClassify.add ? "text-[#FF2800]" : "text-black"
                } text-[14px] leading-[16px] text-right font-bold`}
              >
                {recordClassify.points}
              </div>
              {recordClassify.noDetail ? (
                <div className="w-[18px]"></div>
              ) : (
                <IconArrowToggle
                  className={`ml-[2px] w-[16px] h-[16px] select-none transition-duration-[.3s] transition-transform origin-center ${
                    !showDetail && "rotate-180"
                  }`}
                />
              )}
            </div>
          </div>
          <p className="m-0 mt-[6px] text-[12px] leading-[14px] text-[#9499A0] font-light">
            {dayjs(Number(info.timestamp) * 1000).format("YYYY-MM-DD HH:mm:ss")}
          </p>
        </div>
      </div>
      {showDetail && !recordClassify.noDetail && (
        <figcaption className="mb-[24px]">
          {recordClassify.type === "undraw" && (
            <UnbrewRecord unbrewInfos={unbrewInfos ?? []} info={info} />
          )}
          {recordClassify.type === "redeem" && goodsInfo && (
            <RedeemRecord
              isMobile={isMobile}
              info={info}
              goodsInfo={goodsInfo}
            />
          )}
        </figcaption>
      )}
    </figure>
  );
};

const Record = styled(_Record)`
  .copy-line {
    position: relative;
    &::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 0;
      width: 1px;
      height: 12px;
      background-color: rgba(0, 0, 0, 0.2);
      transform: translateY(-50%);
    }
  }
`;

export default Record;

// 开桶
interface UnbrewRecordProps extends RecordProps {
  unbrewInfos?: Array<PointsBrewingInfo>;
}

const _UnbrewRecord: FC<UnbrewRecordProps> = ({ className, unbrewInfos }) => {
  return (
    <ul
      className={`p-[24px] text-[12px] leading-[20px] font-normal text-[#686868] bg-[#f6f7f8] rounded-[8px] ${className}`}
    >
      {unbrewInfos.map((item, idx) => {
        return (
          <li key={idx} className="pl-[16px] flex items-center unbrew-item">
            <span className="font-normal">{item.desc}</span>
            <span className="flex-1 text-black text-right">
              +{formatThousand(item.income)}
            </span>
          </li>
        );
      })}
    </ul>
  );
};

const UnbrewRecord = styled(_UnbrewRecord)`
  .unbrew-item {
    position: relative;
    &::before {
      content: "";
      position: absolute;
      top: 6px;
      left: 0;
      width: 8px;
      height: 8px;
      border-radius: 100%;
      background-color: #686868;
    }
    &:not(:first-of-type) {
      margin-top: 8px;
      &::after {
        content: "";
        position: absolute;
        top: -10px;
        left: 3px;
        width: 2px;
        height: 12px;
        background-color: #686868;
        transform: scaleX(0.5);
        opacity: 0.4;
      }
    }
  }
`;

interface RedeemRecordProps extends RecordProps {
  goodsInfo: GoodsOrderInfo;
}

const RedeemRecord: FC<RedeemRecordProps> = ({ info, goodsInfo, isMobile }) => {
  const classify = useMemo(() => {
    switch (goodsInfo.goodsType) {
      case "GOODS_POST": {
        return "post";
      }
      case "GOODS_ACTIVATION": {
        return "activation";
      }
      case "GOODS_WHITELIST": {
        return "wl";
      }
      default:
        break;
    }
  }, [goodsInfo.goodsType]);
  return (
    <>
      {classify === "post" && (
        <RedeemPostRecord info={info} goodsInfo={goodsInfo} />
      )}
      {classify === "activation" && (
        <RedeemActivationRecord info={info} goodsInfo={goodsInfo} />
      )}
      {classify === "wl" && (
        <RedeemWLRecord isMobile={isMobile} info={info} goodsInfo={goodsInfo} />
      )}
    </>
  );
};

// 邮寄物品
interface RedeemPostRecordProps extends RedeemRecordProps {}

const RedeemPostRecord: FC<RedeemPostRecordProps> = ({ goodsInfo }) => {
  const { t } = useTranslation("points");
  const { enqueueSnackbar } = useSnackbar();
  const [state, copyToClipboard] = useCopyToClipboard();
  return (
    <div className="flex items-center p-[24px] text-[12px] leading-[20px] font-normal bg-[#f6f7f8] rounded-[8px]">
      <span className="text-[#686868]">{t("post-key")}</span>
      <p className="flex-1 m-0 text-right">
        {goodsInfo.goodsNumber ? (
          <span className="text-black">{goodsInfo.goodsNumber}</span>
        ) : (
          <span className="text-[#FF2800]">{t("posting-tip")}</span>
        )}
      </p>
      {goodsInfo.goodsNumber && (
        <div
          className="ml-[16px] pl-[16px] leading-[20px] text-[#0096ff] copy-line cursor-pointer select-none"
          onClick={() => {
            copyToClipboard(goodsInfo.goodsNumber);
            enqueueSnackbar("Copied successfully", {
              variant: "success",
            });
          }}
        >
          {t("copy")}
        </div>
      )}
    </div>
  );
};

// 激活码类物品
interface RedeemActivationRecordProps extends RedeemRecordProps {}

const RedeemActivationRecord: FC<RedeemActivationRecordProps> = ({
  goodsInfo,
}) => {
  const { t } = useTranslation("points");
  const { enqueueSnackbar } = useSnackbar();
  const [state, copyToClipboard] = useCopyToClipboard();

  let nocodeDesc;
  if ([SubGoodsTypeEnum.CUSTOMIZE_NO_CODE].includes(goodsInfo.subGoodsType)) {
    nocodeDesc = t("nocode-desc");
  } else if (goodsInfo.subGoodsType === SubGoodsTypeEnum.SPRING_GIFT_BAG) {
    nocodeDesc = t("springGiftTips");
  }

  return (
    <div className="flex items-center p-[24px] text-[12px] leading-[20px] font-normal bg-[#f6f7f8] rounded-[8px]">
      {nocodeDesc ? (
        <span className="text-[#686868]">{nocodeDesc}</span>
      ) : (
        <>
          <span className="text-[#686868]">{t("activation-key")}</span>
          <p className="flex-1 m-0 mr-[16px] text-right text-black">
            {goodsInfo.goodsNumber}
          </p>
          <div
            className="pl-[16px] leading-[20px] text-[#0096ff] copy-line cursor-pointer select-none"
            onClick={() => {
              copyToClipboard(goodsInfo.goodsNumber);
              enqueueSnackbar("Copied successfully", {
                variant: "success",
              });
            }}
          >
            {t("copy")}
          </div>
        </>
      )}
    </div>
  );
};

// 白名单兑换
interface RedeemWLRecordProps extends RedeemRecordProps {}

const RedeemWLRecord: FC<RedeemWLRecordProps> = ({
  info,
  goodsInfo,
  isMobile,
}) => {
  const { t } = useTranslation("points");
  const [certificateBlob, setCertificateBlob] = useState(null);
  const certificateRef = useRef();

  const card = useMemo(() => {
    return {
      title: info.desc,
      discordId: goodsInfo.extraWhitelistMsg.discordId,
      time: dayjs(Number(goodsInfo.goodsTime) * 1000).format("MMMM DD, YYYY"),
      certificateNumber: goodsInfo.goodsNumber,
    };
  }, [info, goodsInfo]);

  const isDTCWl = useMemo(() => {
    return goodsInfo.subGoodsType === "WHITELIST_CONTRACT";
  }, [goodsInfo.subGoodsType]);

  useEffect(() => {
    if (!certificateRef.current) return;
    htmlToImage.toBlob(certificateRef.current).then((blob) => {
      setCertificateBlob(blob);
    });
  }, [certificateRef]);

  const certificateImgSrc = useMemo(() => {
    if (!certificateBlob) return null;
    return URL.createObjectURL(certificateBlob);
  }, [certificateBlob]);

  const saveCertificateHandle = () => {
    FileSaver.saveAs(certificateBlob, `${card.title}_proof.png`);
  };

  return (
    <>
      <div className="px-[8px] py-[4px] text-[12px] leading-[16px] text-[#ff2800] rounded-[6px] border-solid border-[1px] border-[#ff2800] bg-[rgba(255,40,0,0.06)]">
        {isDTCWl ? t("wl-desc-dtc") : t("wl-desc")}
      </div>
      <div className="relative mt-[16px] w-[400px] h-[221px] border-solid border-[0.5px] border-[#e3e5e7] rounded-[32px] overflow-hidden">
        {certificateImgSrc ? (
          <Image alt="certificate" src={certificateImgSrc} layout="fill" />
        ) : (
          <div className="absolute scale-[78]">
            <WlCertificate
              elRef={certificateRef}
              title={card.title}
              discordId={card.discordId}
              time={card.time}
              certificateNumber={card.certificateNumber}
              className="w-[512px]"
            />
          </div>
        )}
      </div>
      {!isMobile && (
        <div className="flex justify-end">
          <button
            type="button"
            className="mt-[16px] flex g-[4px] items-center justify-center w-[140px] h-[34px] border-none text-[14px] text-white bg-black rounded-[20px] cursor-pointer hover:opacity-80"
            onClick={saveCertificateHandle}
          >
            <IconSave className="w-[16px] h-[16px]" />
            <span>{t("save-image")}</span>
          </button>
        </div>
      )}
    </>
  );
};
